import type {
  DeviceButtonItem,
  DeviceButtonPayload,
} from '../types/device.types'
import { resolveExtensionId } from '../utils/helpers'
import {
  BUTTON_FUNCTION_OPTIONS,
  CORDLESS_BASE_MODEL_NAMES,
  type DraftButton,
  FUNCTIONS_USING_EXTENSION_VALUE,
  getButtonFunctionOption,
  LEGACY_PARKING_LOTS,
} from './buttonConstants'

let nextSortId = 0
export const makeSortId = () => `btn-${++nextSortId}`

export const normalizeModelNameForComparison = (name: string) =>
  name.replace(/[\s-]+/g, '').toUpperCase()

export const isCordlessBaseModel = (
  modelName: string | undefined | null,
): boolean => {
  if (!modelName) return false
  return CORDLESS_BASE_MODEL_NAMES.has(
    normalizeModelNameForComparison(modelName),
  )
}

export const normalizeIncomingButtonFunction = (value?: string) => {
  const normalized = (value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_')
  if (!normalized) return ''
  if (normalized === 'empty' || normalized === 'blank' || normalized === 'n_a')
    return ''
  if (normalized === 'blf' || normalized === 'blf_presence') return 'presence'
  if (
    normalized === 'shared_voicemail' ||
    normalized === 'shared_voicemail_box'
  )
    return 'voicemail'
  return normalized
}

const parseNumericId = (value?: string) => {
  if (!value) return undefined
  if (!/^\d+$/.test(value)) return undefined
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : undefined
}

export const getDraftButtonFunctionAndExtensionId = (
  button: Pick<
    DeviceButtonPayload,
    'button_function' | 'button_value' | 'extension'
  >,
): { buttonFunction: string; extensionId?: number } => {
  const rawFunction = normalizeIncomingButtonFunction(button.button_function)
  const extensionFromButtonValue = FUNCTIONS_USING_EXTENSION_VALUE.has(
    rawFunction,
  )
    ? parseNumericId(button.button_value)
    : undefined

  const extensionId = resolveExtensionId(button.extension)

  return {
    buttonFunction: rawFunction,
    extensionId: extensionId ?? extensionFromButtonValue,
  }
}

const inferSpeedDialPreset = (
  speedDialValue?: string,
  buttonLabel?: string,
): string | null => {
  if (!speedDialValue) return null
  for (const option of BUTTON_FUNCTION_OPTIONS) {
    if (option.type !== 'speed_dial' || !option.presetValue) continue
    if (option.presetValue !== speedDialValue) continue
    if (
      !option.presetLabel ||
      !buttonLabel ||
      option.presetLabel === buttonLabel
    )
      return option.value
  }
  return null
}

export const formatParkingLotLabel = (lotValue: number): string => {
  return 'P' + (lotValue <= 5999 ? lotValue - 5900 : lotValue - 59000)
}

export function collectUsedParkingLotValues(
  lineDrafts: ReadonlyArray<{ button_function: string; button_value: string }>,
  sidecarDrafts: ReadonlyArray<{
    button_function: string
    button_value: string
  }>,
  exclude: { type: 'LineButton' | 'SidecarButton'; index: number },
): Set<string> {
  const used = new Set<string>()
  const scan = (
    drafts: ReadonlyArray<{ button_function: string; button_value: string }>,
    tableType: 'LineButton' | 'SidecarButton',
  ) => {
    drafts.forEach((d, i) => {
      if (tableType === exclude.type && i === exclude.index) return
      if (getButtonFunctionOption(d.button_function)?.type !== 'parking_slot')
        return
      const v = d.button_value.trim()
      if (v) used.add(v)
    })
  }
  scan(lineDrafts, 'LineButton')
  scan(sidecarDrafts, 'SidecarButton')
  return used
}

export function getFirstAvailableParkingLotValue(used: Set<string>): string {
  for (const lot of LEGACY_PARKING_LOTS) {
    const s = String(lot)
    if (!used.has(s)) return s
  }
  return String(LEGACY_PARKING_LOTS[0])
}

export function toDraft(b: DeviceButtonPayload): DraftButton {
  const { buttonFunction: rawFunction, extensionId } =
    getDraftButtonFunctionAndExtensionId(b)
  const inferredPreset =
    rawFunction === 'speed_dial' || rawFunction === 'dtmf'
      ? inferSpeedDialPreset(b.speed_dial_value, b.button_label)
      : null

  return {
    _sortId: '',
    type: b.type,
    display_count: b.display_count,
    extension_id: extensionId ?? '',
    button_label: b.button_label ?? '',
    button_function:
      inferredPreset ?? (rawFunction === 'empty' ? '' : rawFunction),
    button_value: b.button_value ?? '',
    speed_dial_value: b.speed_dial_value ?? '',
    slot_number: b.slot_number,
  }
}

export function applyButtonFunctionChange(
  draft: DraftButton,
  functionValue: string,
  options?: {
    presenceExtensions?: Array<{ id: number; number: string; name: string }>
    usedParkingLotValues?: Set<string>
  },
): DraftButton {
  const selectedOption = getButtonFunctionOption(functionValue)
  const selectedType = selectedOption?.type
  const updated: DraftButton = {
    ...draft,
    button_function: functionValue,
  }

  if (!selectedType) {
    updated.extension_id = ''
    updated.button_label = ''
    updated.speed_dial_value = ''
    updated.button_value = ''
    delete updated.slot_number
    return updated
  }

  if (selectedType === 'line') {
    updated.speed_dial_value = ''
    updated.button_value = ''
    delete updated.slot_number
    updated.button_label = `Line ${updated.display_count}`
  }

  if (selectedType === 'presence') {
    const ext = options?.presenceExtensions?.[0]
    updated.speed_dial_value = ''
    delete updated.slot_number
    updated.extension_id = ext?.id ?? ''
    updated.button_value = ext ? String(ext.id) : ''
    updated.button_label = ext?.name ?? ''
  }

  if (selectedType === 'voicemail') {
    updated.speed_dial_value = ''
    updated.extension_id = ''
    updated.button_value = ''
    delete updated.slot_number
    updated.button_label = ''
  }

  if (selectedType === 'rollover') {
    updated.speed_dial_value = ''
    updated.button_value = ''
    delete updated.slot_number
    updated.button_label = ''
  }

  if (selectedType === 'parking_slot') {
    updated.speed_dial_value = ''
    updated.extension_id = ''
    if (!updated.button_value) {
      updated.button_value = getFirstAvailableParkingLotValue(
        options?.usedParkingLotValues ?? new Set(),
      )
    }
    const lotValue = Number(updated.button_value)
    updated.button_label = formatParkingLotLabel(lotValue)
  }

  if (selectedType === 'speed_dial') {
    // Parking lots store the lot number in button_value. If that value
    // survives a switch to speed_dial, toDraft re-parses it as an
    // extension PK and the save request fails when no such extension exists.
    updated.extension_id = ''
    updated.button_value = ''
    delete updated.slot_number
    if (selectedOption?.presetValue) {
      updated.speed_dial_value = selectedOption.presetValue
      updated.button_label = selectedOption.presetLabel ?? ''
    } else {
      updated.speed_dial_value = ''
    }
  }

  return updated
}

export function toPayload(d: DraftButton): DeviceButtonPayload {
  const option = getButtonFunctionOption(d.button_function)
  const optionType = option?.type
  const speedDialValue = d.speed_dial_value.trim() || option?.presetValue
  const isParkingSlot = optionType === 'parking_slot'
  const isSpeedDial = optionType === 'speed_dial'

  return {
    type: d.type,
    display_count: d.display_count,
    extension: d.extension_id === '' ? undefined : d.extension_id,
    // Send the raw label (including '') so clearing it on the form actually
    // overwrites the previously stored value on the device.
    button_label: d.button_label,
    button_function: optionType || undefined,
    // Speed dial stores its number in speed_dial_value. Do not pass through
    // leftover parking-lot button_value or toDraft will treat it as an
    // extension PK on the next remount.
    button_value: isSpeedDial ? undefined : d.button_value.trim() || undefined,
    speed_dial_value:
      optionType === 'speed_dial' ? (speedDialValue ?? undefined) : undefined,
    slot_number: isParkingSlot ? d.slot_number : undefined,
  }
}

export function createEmptyDraft(
  type: 'LineButton' | 'SidecarButton',
  position: number,
): DraftButton {
  return {
    _sortId: '',
    type,
    display_count: position,
    extension_id: '',
    button_label: '',
    button_function: type === 'LineButton' && position === 1 ? 'line' : '',
    button_value: '',
    speed_dial_value: '',
  }
}

/**
 * Maps a source device's saved buttons to a payload suitable for the clone
 * mutation. For functions whose `button_value` mirrors the extension PK
 * (line/presence/voicemail/rollover/speed_dial/dtmf), `button_value` is
 * realigned to the resolved extension; for other functions such as
 * `parking_slot` the source `button_value` carries meaningful data (e.g.
 * the parking lot number) and must be preserved verbatim.
 */
export function mapSourceButtonsForClone(
  sourceButtons: ReadonlyArray<DeviceButtonItem>,
  validExtensionIds: ReadonlySet<number>,
): DeviceButtonPayload[] {
  return sourceButtons.map((b) => {
    const extensionPk = resolveExtensionId(b.extension)
    const extensionExists =
      extensionPk != null && validExtensionIds.has(extensionPk)
    const usesExtensionValue = FUNCTIONS_USING_EXTENSION_VALUE.has(
      b.button_function ?? '',
    )
    const buttonValue = usesExtensionValue
      ? extensionExists
        ? String(extensionPk)
        : undefined
      : b.button_value
    return {
      type: b.type,
      display_count: b.display_count,
      button_function: b.button_function,
      button_value: buttonValue,
      button_label: b.button_label,
      extension: extensionExists ? extensionPk : undefined,
      speed_dial_value: b.speed_dial_value,
      slot_number: b.slot_number,
    }
  })
}
